import { AutoAwesome } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import Delete from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Badge,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  Input,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import { captureException } from '@sentry/react';
import { Select as AntSelect, Modal, Tooltip as TooltipLevel, Space } from 'antd';
import BadgeButton from 'components/AdminBooks/BadgeButton';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { DeleteModal } from 'components/Dialog';
import { filter, get, map } from 'lodash';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useCurrentUser } from 'store/users/selectors';
import styled from 'styled-components';
import { getAwsAuthToken } from 'utils/lib/authHelper';
import { moodsOption } from '../workSheet/helper';
import { levelsData, ProficiencyTooltipContent } from '../../components/StoryWithAi/constants';
import sysConf from 'config';
import { isErrorContent } from 'utils/methods/storyContentValidation';
import SoyHistoriasViewAdmin from './SoyHistoriasViewAdmin';
import { handleLearningModuleCount } from 'store/bingo/thunks';
import { handleListStoryAiConfig, handleDeleteStoryAiConfig, handleGetStoryMediaStatus, handleRegenerateStory } from 'store/StoryAiCofig/thunks';
import { useOneStoryWithAi } from 'store/StoryAiCofig/selectors';
import SentryWrap from 'components/Sentry';

const StyledStoryTextField = muiStyled(TextField)(({ theme }) => ({
  backgroundColor: '#fff',
  padding: '5px 15px',
  borderRadius: '12px',
  maxWidth: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
}));

const StyledSelect = muiStyled(Select)(({ theme }) => ({
  '& .MuiSelect-select:focus': {
    backgroundColor: 'transparent !important',
  },
}));

const StyledFormControl = muiStyled(FormControl)(
  ({ theme, width = '77%', borderRadius = '30px' }) => ({
    marginLeft: '10px',
    width: width,
    borderRadius: borderRadius,
    '& .MuiSelect-icon': {
      right: '-11px',
      top: 'calc(50% - 15px)',
    },
  }),
);

const StyledAntSelect = styled(AntSelect)`
  width: 100% !important;

  .ant-select-selector {
    background-color: #fff !important;
    border: 1px solid #d9d9d9 !important;
    border-radius: 30px !important;
    height: 40px !important;
    padding: 4px 11px !important;
    font-size: 14px !important;
    line-height: 1.5715 !important;
    color: rgba(0, 0, 0, 0.85) !important;
    box-shadow: none !important;
    transition:
      border-color 0.3s,
      box-shadow 0.3s !important;

    &:hover {
      border-color: #40a9ff !important;
    }

    &:focus,
    &.ant-select-focused {
      border-color: #40a9ff !important;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
    }
  }

  .ant-select-selection-item {
    line-height: 30px !important;
    color: rgba(0, 0, 0, 0.85) !important;
    font-size: 14px !important;
    font-weight: 600 !important;
  }

  .ant-select-selection-placeholder {
    line-height: 30px !important;
    color: rgba(0, 0, 0, 0.6) !important;
    font-size: 14px !important;
    font-weight: 600 !important;
  }

  .ant-select-arrow {
    right: 11px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    color: rgba(0, 0, 0, 0.45) !important;
    font-size: 12px !important;
    transition: transform 0.3s !important;
  }

  &.ant-select-open .ant-select-arrow {
    transform: translateY(-50%) rotate(180deg) !important;
  }

  .ant-select-dropdown {
    background-color: #fff !important;
    border-radius: 8px !important;
    box-shadow:
      0 6px 16px 0 rgba(0, 0, 0, 0.08),
      0 3px 6px -4px rgba(0, 0, 0, 0.12),
      0 9px 28px 8px rgba(0, 0, 0, 0.05) !important;
    border: 1px solid #d9d9d9 !important;
    padding: 4px 0 !important;
  }

  .ant-select-item {
    background-color: #fff !important;
    padding: 8px 12px !important;
    font-size: 14px !important;
    line-height: 1.5715 !important;
    color: rgba(0, 0, 0, 0.85) !important;
    transition: background-color 0.3s !important;

    &:hover {
      background-color: #f5f5f5 !important;
    }

    &.ant-select-item-option-selected {
      background-color: #e6f7ff !important;
      color: #1890ff !important;
      font-weight: 600 !important;
    }
  }

  .ant-select-item-group {
    background-color: #fff !important;
    padding: 8px 12px 4px 12px !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    color: rgba(0, 0, 0, 0.6) !important;
    border-bottom: 1px solid #f0f0f0 !important;
    margin: 0 !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: #40a9ff !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
  }
`;

const FieldContainer = styled.div`
  label {
    font-size: 15px;
    font-style: normal;
    width: 190px;
    display: flex;
    align-items: center;
  }
  .MuiFormControl-fullWidth {
    margin-left: 10px;
    width: ${({ width }) => width || '77%'};
    border-radius: ${({ borderRadius }) => borderRadius || '30px'};
  }
  .MuiSelect-icon {
    right: -11px;
    top: calc(50% - 15px);
  }
`;

const LessonContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: row;
  margin-top: -20px;
  label {
    width: 190px;
  }
  .MuiFormControl-fullWidth {
    width: 77%;
    margin-left: 10px;
    padding: 0px;
  }
`;

const Label = styled.label`
  color: #fff;
  font-size: 15px;
  font-weight: 400;
`;

const VocContainer = styled.div`
  display: flex;
  width: 100%;
  .MuiAutocomplete-hasClearIcon {
    width: 72%;
    margin-left: 10px;
  }
  .MuiFormControl-fullWidth {
    border-radius: 12px !important;
  }
`;

const TitleContainer = styled.p`
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: white;
`;

const ModalContentWrapper = styled.div`
  background-color: #112643;
  border-radius: 10px;
  padding: 30px 20px;
  max-height: 90vh;
  overflow-y: auto;
`;

const TwoFiledContainer = styled.div`
  display: flex;
  .MuiFormControl-fullWidth {
    margin-left: 10px;
    width: ${({ width }) => width || '77%'};
    border-radius: ${({ borderRadius }) => borderRadius || '30px'};
  }
  .PrivateSwitchBase-root-82 {
    padding: 0px;
  }
`;

const TwoFiledContainerCheck = styled.div`
  label {
    margin-left: 6px;
  }
  display: flex;
  gap: 10px;
  .MuiFormControl-fullWidth {
    padding: 0px !important;
    margin-left: 0px;
    width: ${({ width }) => width || '77%'};
    border-radius: ${({ borderRadius }) => borderRadius || '30px'};
  }
  .PrivateSwitchBase-root-82,
  .PrivateSwitchBase-root-80,
  .MuiIconButton-root {
    padding: 0px !important;
  }
  .MuiTypography-body1 {
    text-transform: unset !important;
  }
`;

const StyledAutocomplete = muiStyled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    padding: 0,
    border: 'none',
    '& fieldset': {
      display: 'none',
    },
  },
  '& .MuiInputBase-root::before, & .MuiInputBase-root::after': {
    display: 'none !important',
  },
  '& .MuiAutocomplete-listbox': {
    display: 'flex',
    flexDirection: 'row',
    overflowX: 'auto',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
  },
  '& .MuiAutocomplete-tag': {
    marginRight: '5px',
    marginBottom: '0',
  },
}));

const languageItems = [
  { value: 'spanish', label: 'Spanish' },
  { value: 'english', label: 'English' }
];

const genresSelection = [
  {
    value: 'realistic_fiction',
    label:
      'Realistic Fiction – A story that could happen in real life with relatable characters and situations.',
  },
  {
    value: 'fantasy',
    label: 'Fantasy – A story that includes magic, mythical creatures, or supernatural elements.',
  },
  {
    value: 'science_fiction',
    label:
      'Science Fiction – A story involving futuristic technology, space exploration, or time travel.',
  },
  {
    value: 'mystery',
    label: 'Mystery – A story centered around solving a problem.',
  },
  {
    value: 'adventure',
    label:
      'Adventure – A story full of action, excitement, and challenges for the main characters.',
  },
  {
    value: 'historical_fiction',
    label:
      'Historical Fiction – A story set in a real historical time period with fictional characters or events.',
  },
  {
    value: 'comedy',
    label:
      'Comedy – A lighthearted and humorous story designed to entertain and make readers laugh.',
  },
  {
    value: 'drama',
    label: 'Drama – A story focusing on deep emotions, relationships, and personal struggles.',
  },
  {
    value: 'fable_folktale',
    label:
      'Fable/Folktale – A short story that teaches a lesson or moral, often with animals or legendary figures.',
  },
  {
    value: 'mythology',
    label:
      'Mythology – A story based on traditional myths, gods, and legends from different cultures.',
  },
  {
    value: 'narrative',
    label: 'Narrative – Tells a story with characters, a setting, and a sequence of events.',
  },
  {
    value: 'argumentative',
    label: 'Argumentative – Presents a clear argument and supports it with reasons and evidence.',
  },
  {
    value: 'informative',
    label:
      'Informative – Explains or describes a topic to give the reader new information or understanding.',
  },
];

const wordCountData = [
  { value: '100-150', label: '100-150 words' },
  { value: '150-200', label: '150-200 words' },
  { value: '200-250', label: '200-250 words' },
  { value: '250-300', label: '250-300 words' },
  { value: '300-350', label: '300-350 words' },
  { value: '350-400', label: '350-400 words' },
  { value: '400-450', label: '400-450 words' },
];

const gradeLevelData = [
  { value: 3, label: '3rd grade' },
  { value: 4, label: '4th grade' },
  { value: 5, label: '5th grade' },
  { value: 6, label: '6th grade' },
  { value: 7, label: '7th grade' },
  { value: 8, label: '8th grade' },
  { value: 9, label: '9th grade' },
  { value: 10, label: '10th grade' },
  { value: 11, label: '11th grade' },
  { value: 12, label: '12th grade' },
  { value: 'college', label: 'College level' },
];

const useStyles = () => ({
  menuItemText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '150px',
    display: 'block',
  },
  select: {},
});

const GenreTooltipContent = () => (
  <div>
    <ul style={{ paddingLeft: 16, margin: 0 }}>
      {genresSelection.map((item, index) => (
        <li key={index} style={{ fontSize: 13, marginBottom: 4, textTransform: 'capitalize' }}>
          {item.label}
        </li>
      ))}
    </ul>
  </div>
);

const SoyHistoriasAdmin = ({ page, chapter, main_book_data, isResource = false }) => {
  const [open, setOpen] = useState(false);
  const [gradeLevel, setGradeLevel] = useState('');
  const [genres, setGenres] = useState('');
  const [proficiencyLevel, setProficiencyLevel] = useState('');
  const [moodTense, setMoodTense] = useState('');
  const [wordCount, setWordCount] = useState('');
  const [language, setLanguage] = useState('Spanish');
  const [ownVocab, setOwnVocab] = useState('');
  const [storyPlot, setStoryPlot] = useState('');
  const [vocabInput, setVocabInput] = useState('');
  const [selectableContent, setSelectableContent] = useState([]);
  const [fromPageIndex, setFromPageIndex] = useState(0);
  const [toPageId, setToPageId] = useState(null);
  const [pageContentCheck, setPageContentCheck] = useState(true);
  const [pageVocabCheck, setPageVocabCheck] = useState(false);
  const [selectedFromPage, setSelectedFromPage] = useState('');
  const [selectedToPage, setSelectedToPage] = useState('');
  const [fromPage, setFromPage] = useState(null);
  const [openDropDown, setOpenDropDown] = useState({
    gradeLevel: false,
    genre: false,
    levelsData: false,
    wordCountData: false,
    language: false,
    from_page: false,
    to_page: false,
  });
  const classes = useStyles();

  const [handleStoryStream, setHandleStoryStream] = useState('');
  const [handleStoryStreamTitle, setHandleStoryStreamTitle] = useState('');
  const currentUser = useCurrentUser();
  const showAlert = useAlert();
  const dispatch = useDispatch();
  const [showStoryView, setShowStoryView] = useState(false);
  const storyViewRef = useRef(null);
  const imagesRef = useRef([]);
  const prevStoryIdRef = useRef(null);
  const getStoryWithAi = useOneStoryWithAi();
  const [hasStoryWithAi, setHasStoryWithAi] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [selectedStoryId, setSelectedStoryId] = useState(null);
  const [newlyCreatedStoryTitle, setNewlyCreatedStoryTitle] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [regenerateLoader, setRegenerateLoader] = useState(false);

  const formattedStoryData = useMemo(() => {
    return hasStoryWithAi
      ?.filter((item) => !isErrorContent(item?.story_content || ''))
      ?.map((item) => ({
        value: item.id,
        label: item.story_title,
      }));
  }, [hasStoryWithAi]);

  const fetchStoryWithAi = async () => {
    if(!currentUser?.id || !chapter) return;
    dispatch(
      handleListStoryAiConfig(
        {
          chapter,
          user_id: get(currentUser, 'id', ''),
          class_id: '',
        },
        false,
      ),
    );
  };

  useEffect(() => {
    fetchStoryWithAi();
  }, [currentUser, chapter, isResource]);
  

  useEffect(() => {
    const filterByPage =
      filter(getStoryWithAi, (item) => {
        const pageMatch = isResource ?  item.page == page : parseInt(item.page) === parseInt(page);
        const roleMatch = item?.role === 'owe_admin' ? true : false;
        const isNotError = !isErrorContent(item?.story_content || '');
        return pageMatch && isNotError && roleMatch;
      }) || [];
    
    const sortedStories = [...filterByPage].sort(
      (a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0)
    );
    
    setHasStoryWithAi(sortedStories);
    
    if (newlyCreatedStoryTitle && sortedStories?.length > 0) {
      const newStory = sortedStories.find(
        (item) => {
          const titleMatch = item.story_title === newlyCreatedStoryTitle || 
                            item.story_title?.includes(newlyCreatedStoryTitle) ||
                            newlyCreatedStoryTitle?.includes(item.story_title);
          return titleMatch;
        }
      );
      if (newStory) {
        setSelectedStory(newStory);
        setSelectedStoryId(newStory.id);
        setNewlyCreatedStoryTitle(null);
        setShowStoryView(true);
        setHandleStoryStream('');
        setHandleStoryStreamTitle('');
        setIsStreaming(false);
        resetAllFields();
        return;
      } else {
        const mostRecentStory = sortedStories[0];
        if (mostRecentStory) {
          setSelectedStory(mostRecentStory);
          setSelectedStoryId(mostRecentStory.id);
          setNewlyCreatedStoryTitle(null);
          setShowStoryView(true);
          setHandleStoryStream('');
          setHandleStoryStreamTitle('');
          setIsStreaming(false);
          resetAllFields();
          return;
        }
      }
    }
    
    if (sortedStories?.length > 0 && !selectedStoryId && !newlyCreatedStoryTitle) {
      setSelectedStory(sortedStories[0]);
      setSelectedStoryId(sortedStories[0]?.id);
    }
  }, [page, getStoryWithAi, newlyCreatedStoryTitle]);

  useEffect(() => {
    if (selectedStoryId && hasStoryWithAi?.length > 0) {
      const story = hasStoryWithAi.find((item) => item.id === selectedStoryId);
      if (story) {
        setSelectedStory(story);
      }
    }
  }, [selectedStoryId, hasStoryWithAi]);

  const resetAllFields = () => {
    setHandleStoryStream('');
    setHandleStoryStreamTitle('');
    setGradeLevel('');
    setGenres('');
    setProficiencyLevel('');
    setMoodTense('');
    setWordCount('');
    setLanguage('Spanish');
    setOwnVocab('');
    setStoryPlot('');
    setVocabInput('');
    setIsStreaming(false);
  };

  useEffect(() => {
    const contents = main_book_data?.pages || [];
    setSelectableContent(
      contents.map((k, i) => ({
        ...k,
        dynamic_id: i + 1,
        numberMasked: k.NumberMasked || k.numberMasked || k.dynamic_id || i + 1,
        pageId: k.pageId || k.page_id || k.id || String(i + 1),
      })),
    );
  }, [main_book_data]);

  const moodTenseOptions = useMemo(() => {
    return moodsOption.map((mood) => ({
      label: mood.label,
      options: mood.tenseOptions.map((tense) => ({
        label: tense.label || mood.label,
        value: `${mood.label}-${tense.label || mood.label}`,
      })),
    }));
  }, []);

  const currentTokens = useMemo(
    () =>
      ownVocab
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [ownVocab],
  );

  const formatDisplayValue = (value) => {
    if (!value) return undefined;
    const [mood, tense] = value.split('-');
    return `${tense} (${mood})`;
  };

  const updateState = (name, value) => {
    setOpenDropDown((prev) => ({ ...prev, [name]: value }));
  };

  const handleMenuOpen = (value) => {
    updateState(value, true);
  };

  const handleMenuClose = (value) => {
    updateState(value, false);
  };

  const commitTokens = (parts) => {
    const cleaned = parts.map((s) => s.trim()).filter(Boolean);
    if (!cleaned.length) return;
    const merged = Array.from(new Set([...currentTokens, ...cleaned]));
    setOwnVocab(merged.join(','));
  };

  const handleToPageChange = (event) => {
    const selectedPageValue = event.target.value;
    const selectedIndex = selectableContent.findIndex(
      (bk) => (bk.NumberMasked || bk.numberMasked || bk.dynamic_id) == selectedPageValue,
    );
    if (selectedIndex >= 0) {
      const selectedPageId = selectableContent[selectedIndex].pageId;
      if (fromPage && parseInt(selectedPageId) - parseInt(fromPage) > 2) {
        alert('Maximum 3 pages can be select.');
      } else {
        setToPageId(selectedPageId);
        setSelectedToPage(event.target.value);
      }
    }
  };

  const handleFromPageChange = (event) => {
    const selectedPageValue = event.target.value;
    const selectedIndex = selectableContent.findIndex(
      (bk) => (bk.NumberMasked || bk.numberMasked || bk.dynamic_id) == selectedPageValue,
    );
    if (selectedIndex >= 0) {
      const selectedPageId = selectableContent[selectedIndex].pageId;
      if (toPageId && parseInt(toPageId) - parseInt(selectedPageId) > 2) {
        alert('Maximum 3 pages can be select.');
        return;
      }
    }
    if (selectableContent[selectedIndex]) {
      setFromPage(selectableContent[selectedIndex].pageId);
    }
    if (selectableContent[selectedIndex]?.pageId > toPageId) {
      setToPageId(null);
      setSelectedToPage('');
    }
    setSelectedFromPage(selectedPageValue);
    setFromPageIndex(selectedIndex);
  };

  const regenerateStory = async (storyId) => {
    try {
      setRegenerateLoader(true);
    
      const mediaStatus = await dispatch(handleGetStoryMediaStatus(storyId));
      if (!mediaStatus) {
        setRegenerateLoader(false);
        return;
      }

        if (mediaStatus?.created_at) {
          const createdTime = new Date(mediaStatus.created_at).getTime();
          const currentTime = new Date().getTime();
          const timeDifference = (currentTime - createdTime) / 1000;
          
          if (timeDifference < 30) {
            showAlert('Media is  generating. Please wait for it to complete or regenerate the story again.', 'info');
            setRegenerateLoader(false);
            return;
          }
        } else if(mediaStatus?.api_in_progress) {
          showAlert('Media is generating. Please wait for it to complete or regenerate the story again.', 'info');
          setRegenerateLoader(false);
          return;
        }
      
      const bothGenerated = mediaStatus?.is_audio_generated && mediaStatus?.is_image_generated;
      if (bothGenerated) {
        Modal.confirm({
          title: 'Regenerate story',
          content: 'Are you sure you want to regenerate story audio and image again?',
          okText: 'Yes, regenerate',
          cancelText: 'Cancel',
          onOk: async () => {
            try {
              await dispatch(handleRegenerateStory({ storyId, is_audio_generated: false, is_image_generated: false }));
              showAlert('Regeneration started. Audio and images will update when ready.', 'info');
            } finally {
              setRegenerateLoader(false);
            }
          },
          onCancel: () => {
            setRegenerateLoader(false);
          },
        });
      } else {
        Modal.confirm({
          title: 'Regenerate story',
          content: 'Are you sure you want to regenerate story',
          okText: 'Yes, regenerate',
          cancelText: 'Cancel',
          onOk: async () => {
            try {
              await dispatch(handleRegenerateStory({
                storyId,
                is_audio_generated: mediaStatus?.is_audio_generated || false,
                is_image_generated: mediaStatus?.is_image_generated || false
              }));
              showAlert('Regeneration started. Missing media will be generated.', 'info');
            } finally {
              setRegenerateLoader(false);
            }
          },
          onCancel: () => {
            setRegenerateLoader(false);
          },
        });
      }
    } catch (error) {
      console.error('Error regenerating story:', error);
      showAlert('Failed to regenerate story. Please try again.', 'error');
      setRegenerateLoader(false);
    }
  };

  async function streamData(inputData, onChunk) {
    try {
      const token = await getAwsAuthToken();
      const localUrl = 'http://localhost:4000/StoryAI/story-ai-stream';
      const prodUrl = `${sysConf.apiGateway.AI_BASE_NODE_URL}/StoryAI/story-ai-stream`;
      const response = await fetch(prodUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-source': sysConf.isDev ? 'test' : 'prod',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(inputData),
      });

      if (!response.ok || !response.body) {
        throw new Error('Stream failed to start');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let pendingEvent = null;
      let title = '';
      let story = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split('\n').filter(Boolean);

        for (const line of lines) {
          if (line.startsWith('event:')) {
            pendingEvent = line.replace('event:', '').trim();
            continue;
          }

          if (!line.startsWith('data:')) continue;

          const data = line.replace('data:', '').trim();

          if (data === '[DONE]') {
            if (isErrorContent(story)) {
              setIsStreaming(false);
              showAlert('Content is not available. Please try generating it again.', 'error');
              setOpen(false);
              setHandleStoryStream('');
              setHandleStoryStreamTitle('');
              setNewlyCreatedStoryTitle(null);
              resetAllFields();
              return;
            }

            setOpen(false);

            const response = await dispatch(
              handleListStoryAiConfig(
                {
                  chapter,
                  user_id: get(currentUser, 'id', ''),
                  class_id: '',
                  role: currentUser?.role,
                },
                false,
              ),
            );

            if (title && response?.success && response?.data) {
              const allStories = response.data || [];
              const filterByPage = filter(allStories, (item) => {
                const pageMatch = isResource ? item.page == page : parseInt(item.page) === parseInt(page);
                const roleMatch = item?.role === 'owe_admin' ? true : false;
                const isNotError = !isErrorContent(item?.story_content || '');
                return pageMatch && isNotError && roleMatch;
              }) || [];

              const sortedStories = [...filterByPage].sort(
                (a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0)
              );

              const newStory = sortedStories.find(
                (item) => {
                  const titleMatch = item.story_title === title || 
                                    item.story_title?.includes(title) ||
                                    title?.includes(item.story_title);
                  return titleMatch;
                }
              );

              if (newStory) {
                setSelectedStory(newStory);
                setSelectedStoryId(newStory.id);
                setHasStoryWithAi(sortedStories);
                setIsStreaming(false);
                setHandleStoryStream('');
                setHandleStoryStreamTitle('');
                resetAllFields();
              } else if (sortedStories?.length > 0) {
                setSelectedStory(sortedStories[0]);
                setSelectedStoryId(sortedStories[0].id);
                setHasStoryWithAi(sortedStories);
                setIsStreaming(false);
                setHandleStoryStream('');
                setHandleStoryStreamTitle('');
                resetAllFields();
              } else {
                setIsStreaming(false);
              }
            } else {
              setIsStreaming(false);
            }

            return;
          }

          if (pendingEvent === 'title') {
            try {
              title = JSON.parse(data);
              onChunk({ type: 'title', content: title });
            } catch (err) {
              console.error('Failed to parse title JSON:', data);
            }
            pendingEvent = null;
            continue;
          }

          try {
            const parsed = JSON.parse(data);

            if (parsed.type === 'chunk') {
              story += parsed.content;
              onChunk({ type: 'chunk', content: parsed.content });
            }

            if (parsed.type === 'error') {
              console.error('Server error:', parsed.message);
              setIsStreaming(false);
              showAlert(parsed.message || 'Failed to generate story. Please try again.', 'error');
              setOpen(false);
              setHandleStoryStream('');
              setHandleStoryStreamTitle('');
              setNewlyCreatedStoryTitle(null);
              resetAllFields();
              return;
            }
          } catch (err) {
            console.error('Failed to parse JSON:', data);
          }
        }
      }
    } catch (error) {
      console.error('Stream error:', error);
      setIsStreaming(false);
      showAlert('An error occurred while generating the story. Please try again.', 'error');
      setHandleStoryStream('');
      setHandleStoryStreamTitle('');
      setNewlyCreatedStoryTitle(null);
      setOpen(false);
      resetAllFields();
    }
  }

  const GenerateStory = async () => {
    try {
      const payload = {
        grade_level: gradeLevel,
        genres,
        wordCount,
        proficiencyLevel,
        language,
        story_plot: storyPlot,
        ownVocab,
        page_content: {
          fromPage: fromPage,
          toPage: toPageId,
        },
        based_on: {
          page_content: pageContentCheck,
          page_vocab: pageVocabCheck,
        },
        mood_tense: moodTense,
      };

      const data = {
        user_id: get(currentUser, 'id', ''),
        //class_id: get(currentClass, 'id', ''),
        class_id: '',
        chapter,
        page,
        content: payload,
        // resource: from !== 'ebook' ? true : false,
        resource: false,
        mode: import.meta.env.VITE_APP_STAGE?.trim() === 'prod' ? 'prod' : 'dev',
        role: 'owe_admin',
      };
      setHandleStoryStream('');
      setHandleStoryStreamTitle('');
      setSelectedStory(null);
      setSelectedStoryId(null);
      setNewlyCreatedStoryTitle(null);
      setShowStoryView(true);
      setIsStreaming(true);
      let fullHTML = '';
      let storyTitle = '';
      await streamData(data, (streamData) => {
        if (streamData.type === 'title') {
          storyTitle = streamData.content;
          setHandleStoryStreamTitle(streamData.content);
        } else if (streamData.type === 'chunk') {
          fullHTML += streamData.content;
          setHandleStoryStream((prev) => prev + streamData.content);
        }
      });
    } catch (error) {
      captureException(error);
      console.log(error);
      setIsStreaming(false);
      setOpen(false);
      setShowStoryView(false);
      setHandleStoryStream('');
      setHandleStoryStreamTitle('');
      setNewlyCreatedStoryTitle(null);
      resetAllFields();
      showAlert('An error occurred while generating the story. Please try again.', 'error');
    }
  };

  const onDeleteStory = async () => {
    try {
      setDeleteLoader(true);
      await dispatch(handleDeleteStoryAiConfig(selectedStory?.id));
      setDeleteLoader(false);
      setDeleteModal(false);
      dispatch(handleLearningModuleCount({
        chapter: selectedStory?.chapter,
        userId: currentUser.id,
        pageId: selectedStory?.page,
      }));
      if (selectedStoryId === selectedStory?.id) {
        const deletedIndex = hasStoryWithAi?.findIndex(
          (story) => story.id === selectedStory?.id
        );

        const updatedStories = hasStoryWithAi?.filter(
          (story) => story.id !== selectedStory?.id
        );

        if(updatedStories?.length === 0){
          setShowStoryView(false);
        }

        let nextSelectedStory = null;
        if (updatedStories?.length) {
          const newIndex = deletedIndex > 0 ? deletedIndex - 1 : 0;
          nextSelectedStory = updatedStories[newIndex];
        }

        setSelectedStory(nextSelectedStory);
        setSelectedStoryId(nextSelectedStory?.id);
        setHandleStoryStreamTitle('');
        setHandleStoryStream('');
      }
      fetchStoryWithAi();
    } catch (error) {
      captureException(error);
      console.log(error);
    }
  };

  return (
    <SentryWrap>
      <BadgeButton
        onClick={() => {
          if (hasStoryWithAi?.length > 0) {
            setSelectedStory(hasStoryWithAi[0]);
            setSelectedStoryId(hasStoryWithAi[0]?.id);
            setShowStoryView(true);
          } else {
            setOpen(true);
          }
        }}
        startIcon={<AutoAwesome />}
        showBadge={hasStoryWithAi?.length > 0 ? true : false}
        backgroundColor="#2d7d32"
      >
        {hasStoryWithAi?.length > 0 ? 'View Soy Historias' : 'Generate Soy Historias'}
      </BadgeButton>

      {showStoryView && (isStreaming || selectedStory) ? (
        <Modal
          open={showStoryView}
          onCancel={() => {
            setShowStoryView(false);
            setHandleStoryStream('');
            setHandleStoryStreamTitle('');
            setSelectedStory(null);
            setSelectedStoryId(null);
            setIsStreaming(false);
          }}
          footer={null}
          width={900}
          style={{ top: 20 }}
          bodyStyle={{ padding: 0 }}
          closable={false}
        >
          {isStreaming ? (
            <>
              <div
                style={{
                  position: 'relative',
                  padding: '15px 20px',
                  backgroundColor: '#112643',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '10px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span
                    style={{
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Streaming...
                  </span>
                  <span
                    onClick={() => {
                      resetAllFields();
                      setIsStreaming(false);
                      setShowStoryView(false);
                      setHandleStoryStream('');
                      setHandleStoryStreamTitle('');
                      setSelectedStory(null);
                      setSelectedStoryId(null);
                    }}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CancelIcon
                      style={{
                        fontSize: 22,
                        color: 'red',
                        background: 'white',
                        borderRadius: 20,
                        padding: '2px',
                      }}
                    />
                  </span>
                </div>
              </div>
              <SoyHistoriasViewAdmin
                ref={storyViewRef}
                text={handleStoryStream || ''}
                title={handleStoryStreamTitle || ''}
                images={[]}
                storyInfo={{}}
                imageConfiguration={{}}
                isStream={true}
              />
            </>
          ) : selectedStory ? (
            <>
              <div
                style={{
                  position: 'relative',
                  padding: '15px 20px',
                  backgroundColor: '#112643',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '15px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                  {hasStoryWithAi?.length > 0 && formattedStoryData?.length > 0 && (
                    <>
                      <label
                        style={{
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Select Story:
                      </label>
                      <Badge
                        overlap="rectangular"
                        badgeContent={
                          formattedStoryData.length > 0 ? formattedStoryData?.length : undefined
                        }
                        color="info"
                        variant="standard"
                        sx={{ flex: 1, maxWidth: '300px' }}
                      >
                        <AntSelect
                          style={{ width: '100%' }}
                          placeholder="Select a story"
                          value={selectedStoryId}
                          onChange={(value) => {
                            const story = hasStoryWithAi?.find((item) => item.id === value);
                            if (story) {
                              setSelectedStory(story);
                              setSelectedStoryId(value);
                              setHandleStoryStream('');
                              setHandleStoryStreamTitle('');
                            }
                          }}
                          className="dropdown-select"
                        >
                          {formattedStoryData.map((item) => (
                            <AntSelect.Option key={item.value} value={item.value}>
                              {item.label}
                            </AntSelect.Option>
                          ))}
                        </AntSelect>
                      </Badge>
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <BadgeButton
                    onClick={() => selectedStoryId && regenerateStory(selectedStoryId)}
                    style={{ height: '32px', backgroundColor: '#017216 !important', fontSize: '13px', padding: '0 15px', width: "150px" }}
                    disabled={regenerateLoader}
                  >
                    {regenerateLoader ? 'Regenerating...' : 'Regenerate Image'}
                  </BadgeButton>
                  <BadgeButton
                    onClick={() => {
                      resetAllFields();
                      setIsStreaming(false);
                      setShowStoryView(false);
                      setHandleStoryStream('');
                      setHandleStoryStreamTitle('');
                      setSelectedStory(null);
                      setSelectedStoryId(null);
                      setOpen(true);
                    }}
                    backgroundColor="#017216"
                    style={{ height: '32px', fontSize: '13px', padding: '0 15px' }}
                  >
                    Create Story
                  </BadgeButton>
                  {selectedStory && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteModal(true);
                      }}
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Delete
                        style={{
                          fontSize: 22,
                          color: 'red',
                          background: 'white',
                          borderRadius: 20,
                          padding: '2px',
                        }}
                      />
                    </span>
                  )}
                  <span
                    onClick={() => {
                      resetAllFields();
                      setIsStreaming(false);
                      setShowStoryView(false);
                      setHandleStoryStream('');
                      setHandleStoryStreamTitle('');
                      setSelectedStory(null);
                      setSelectedStoryId(null);
                    }}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CancelIcon
                      style={{
                        fontSize: 22,
                        color: 'red',
                        background: 'white',
                        borderRadius: 20,
                        padding: '2px',
                      }}
                    />
                  </span>
                </div>
              </div>
              <SoyHistoriasViewAdmin
                ref={storyViewRef}
                text={selectedStory?.story_content || ''}
                title={selectedStory?.story_title || ''}
                images={selectedStory?.images || []}
                storyInfo={selectedStory || {}}
                imageConfiguration={{}}
                isStream={false}
              />
            </>
          ) : null}
        </Modal>
      ) : null}

      <Modal
        open={open}
        onCancel={() => {
          setOpen(false);
          setHandleStoryStream('');
          setHandleStoryStreamTitle('');
        }}
        footer={null}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ padding: 0 }}
        closable={false}
      >
        <ModalContentWrapper>
          <span
            onClick={() => {
              setOpen(false);
              setHandleStoryStream('');
              setHandleStoryStreamTitle('');
            }}
            style={{
              position: 'absolute',
              right: 10,
              top: 10,
              cursor: 'pointer',
              zIndex: 10,
            }}
          >
            <CancelIcon
              style={{
                fontSize: 22,
                color: 'red',
                background: 'white',
                borderRadius: 20,
              }}
            />
          </span>

          <>
            <TitleContainer>Generate Soy Historias With AI</TitleContainer>

              <FieldContainer className="allCreateMyLession">
                <label>Grade Level</label>
                <StyledFormControl fullWidth>
                  <StyledSelect
                    input={<Input disableUnderline />}
                    value={gradeLevel}
                    onChange={(e) => setGradeLevel(e.target.value)}
                    displayEmpty
                    open={openDropDown?.gradeLevel}
                    onOpen={() => handleMenuOpen('gradeLevel')}
                    onClose={() => handleMenuClose('gradeLevel')}
                    inputProps={{ 'aria-label': 'Without label' }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                      disableScrollLock: true,
                    }}
                    renderValue={(selected) => {
                      if (!selected) return <span>Select</span>;
                      const selectedItem = gradeLevelData.find((item) => item.value === selected);
                      return selectedItem ? selectedItem.label : selected;
                    }}
                  >
                    {gradeLevelData.map((item, index) => (
                      <MenuItem key={index} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </StyledFormControl>
              </FieldContainer>

              <FieldContainer className="allCreateMyLession">
                <label>
                  Genre
                  <TooltipLevel
                    arrow={false}
                    trigger={['click']}
                    title={<GenreTooltipContent />}
                    placement="top"
                    overlayStyle={{ maxWidth: window.innerWidth <= 740 ? 300 : 500 }}
                    color={'#3d5f8f'}
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <InfoIcon
                        style={{
                          fontSize: 25,
                          color: 'white',
                          marginLeft: '8px',
                          cursor: 'pointer',
                        }}
                      />
                    </span>
                  </TooltipLevel>
                </label>
                <FormControl fullWidth>
                  <Select
                    className={classes.select}
                    value={genres}
                    onChange={(e) => setGenres(e.target.value)}
                    displayEmpty
                    open={openDropDown?.genre}
                    onOpen={() => handleMenuOpen('genre')}
                    onClose={() => handleMenuClose('genre')}
                    inputProps={{ 'aria-label': 'Without label' }}
                    input={<Input disableUnderline />}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                      disableScrollLock: true,
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span>Select</span>;
                      }
                      const selectedItem = genresSelection.find((item) => item.label === selected);
                      return (
                        <Tooltip title={selectedItem ? selectedItem.label : ''} placement="top">
                          <span className={classes.menuItemText}>
                            {selectedItem && typeof selectedItem.label === 'string'
                              ? selectedItem.label
                              : ''}
                          </span>
                        </Tooltip>
                      );
                    }}
                  >
                    {genresSelection.map((item, index) => (
                      <MenuItem style={{ maxWidth: 500 }} key={index} value={item?.label}>
                        <TooltipLevel zIndex={9999999} color={'#3d5f8f'} title={item?.label}>
                          <span className="text-wrap">
                            {item?.label && typeof item.label === 'string' ? item?.label : ''}
                          </span>
                        </TooltipLevel>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FieldContainer>

              <FieldContainer className="allCreateMyLession">
                <label>Word Count</label>
                <FormControl fullWidth>
                  <Select
                    input={<Input disableUnderline />}
                    className={classes.select}
                    value={wordCount}
                    onChange={(e) => setWordCount(e.target.value)}
                    displayEmpty
                    open={openDropDown?.wordCountData}
                    onOpen={() => handleMenuOpen('wordCountData')}
                    onClose={() => handleMenuClose('wordCountData')}
                    inputProps={{ 'aria-label': 'Without label' }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                      disableScrollLock: true,
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span>Select</span>;
                      }
                      const selectedItem = wordCountData.find((item) => item.label === selected);
                      return (
                        <Tooltip title={selectedItem ? selectedItem.label : ''} placement="top">
                          <span className={classes.menuItemText}>{selectedItem.label ?? ''}</span>
                        </Tooltip>
                      );
                    }}
                  >
                    {wordCountData.map((item, index) => (
                      <MenuItem key={index} value={item?.label}>
                        {item?.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FieldContainer>

              <FieldContainer
                width={'77%'}
                className="allCreateMyLession proficiy"
                borderRadius={'30px'}
              >
                <label>
                  Proficiency Level
                  <TooltipLevel
                    arrow={false}
                    title={<ProficiencyTooltipContent />}
                    placement="top"
                    overlayStyle={{ maxWidth: window.innerWidth <= 740 ? 300 : 500 }}
                    color={'#3d5f8f'}
                    trigger={['click']}
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <InfoIcon
                        style={{
                          fontSize: 25,
                          color: 'white',
                          marginLeft: '8px',
                          cursor: 'pointer',
                        }}
                      />
                    </span>
                  </TooltipLevel>
                </label>
                <FormControl fullWidth>
                  <Select
                    input={<Input disableUnderline />}
                    className={classes.select}
                    value={proficiencyLevel}
                    onChange={(e) => setProficiencyLevel(e.target.value)}
                    displayEmpty
                    open={openDropDown?.levelsData}
                    onOpen={() => handleMenuOpen('levelsData')}
                    onClose={() => handleMenuClose('levelsData')}
                    inputProps={{ 'aria-label': 'Without label' }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                      disableScrollLock: true,
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span>Select</span>;
                      }
                      const selectedItem = levelsData.find((item) => item.label === selected);
                      return (
                        <Tooltip title={selectedItem ? selectedItem.label : ''} placement="top">
                          <span className={classes.menuItemText}>
                            {selectedItem && typeof selectedItem.label === 'string'
                              ? selectedItem.label
                              : ''}
                          </span>
                        </Tooltip>
                      );
                    }}
                  >
                    {levelsData.map((item, index) => (
                      <MenuItem style={{ maxWidth: 500 }} key={index} value={item?.label}>
                        <TooltipLevel zIndex={9999999} color={'#3d5f8f'} title={item?.label}>
                          <span className="text-wrap">
                            {item?.label && typeof item.label === 'string' ? item?.label : ''}
                          </span>
                        </TooltipLevel>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FieldContainer>

              <FieldContainer
                width={'77%'}
                className="allCreateMyLession proficiy"
                borderRadius={'30px'}
              >
                <label>Mood / Tense</label>
                <FormControl
                  fullWidth
                  style={{
                    backgroundColor: 'transparent',
                    padding: '0px',
                    paddingTop: '0px !important',
                  }}
                >
                  <StyledAntSelect
                    placeholder="Select"
                    value={
                      moodTense
                        ? { value: moodTense, label: formatDisplayValue(moodTense) }
                        : undefined
                    }
                    onChange={(value) => setMoodTense(value.value)}
                    options={moodTenseOptions}
                    showSearch
                    labelInValue
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </FormControl>
              </FieldContainer>

              {isResource ? null : (
                <FieldContainer className="allCreateMyLession">
                  <label>Select Page Content</label>
                  <TwoFiledContainer style={{ width: '79%' }} width={'50%'}>
                    <FormControl fullWidth>
                      <Select
                        input={<Input disableUnderline />}
                        className={classes.select}
                        value={selectedFromPage}
                        onChange={handleFromPageChange}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Without label' }}
                        open={openDropDown?.from_page}
                        onOpen={() => handleMenuOpen('from_page')}
                        onClose={() => handleMenuClose('from_page')}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                          getContentAnchorEl: null,
                          disableScrollLock: true,
                        }}
                      >
                        {map(selectableContent, (bk, index) => {
                          const displayValue =
                            bk.NumberMasked || bk.numberMasked || bk.dynamic_id || index + 1;
                          return (
                            <MenuItem key={index} value={displayValue}>
                              {displayValue}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <Select
                        input={<Input disableUnderline />}
                        className={classes.select}
                        value={selectedToPage}
                        disabled={!selectedFromPage}
                        onChange={handleToPageChange}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Without label' }}
                        open={openDropDown?.to_page}
                        onOpen={() => handleMenuOpen('to_page')}
                        onClose={() => handleMenuClose('to_page')}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                          getContentAnchorEl: null,
                          disableScrollLock: true,
                        }}
                      >
                        {map(selectableContent.slice(fromPageIndex), (bk, index) => {
                          const displayValue =
                            bk.NumberMasked ||
                            bk.numberMasked ||
                            bk.dynamic_id ||
                            fromPageIndex + index + 1;
                          return (
                            <MenuItem key={index} value={displayValue}>
                              {displayValue}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </TwoFiledContainer>
                </FieldContainer>
              )}

              <FieldContainer className="allCreateMyLession">
                <label style={{ minWidth: 'fit-content' }}>Create Base On</label>
                <TwoFiledContainerCheck width={'50%'} className="createbaseOnStyling">
                  <FormControl fullWidth>
                    <FormGroup row>
                      <FormControlLabel
                        label={<span style={{ color: 'black' }}>Page Vocab</span>}
                        control={
                          <Checkbox
                            checked={pageVocabCheck}
                            onChange={(e) => setPageVocabCheck(e.target.checked)}
                            sx={{
                              '&.Mui-checked': {
                                color: '#f50057',
                              },
                            }}
                          />
                        }
                      />
                    </FormGroup>
                  </FormControl>
                  <FormControl fullWidth>
                    <FormGroup row>
                      <FormControlLabel
                        label={<span style={{ color: 'black' }}>Page Content</span>}
                        control={
                          <Checkbox
                            checked={pageContentCheck}
                            onChange={(e) => setPageContentCheck(e.target.checked)}
                            sx={{
                              '&.Mui-checked': {
                                color: '#f50057',
                              },
                            }}
                          />
                        }
                      />
                    </FormGroup>
                  </FormControl>
                </TwoFiledContainerCheck>
              </FieldContainer>

              <FieldContainer className="allCreateMyLession">
                <label>Language</label>
                <FormControl fullWidth>
                  <Select
                    input={<Input disableUnderline />}
                    className={classes.select}
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    displayEmpty
                    open={openDropDown?.language}
                    onOpen={() => handleMenuOpen('language')}
                    onClose={() => handleMenuClose('language')}
                    inputProps={{ 'aria-label': 'Without label' }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                      disableScrollLock: true,
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span>Select</span>;
                      }
                      const selectedItem = languageItems.find((item) => item.label === selected);
                      return (
                        <Tooltip title={selectedItem ? selectedItem.label : ''} placement="top">
                          <span className={classes.menuItemText}>
                            {selectedItem
                              ? selectedItem.label?.length > 10
                                ? selectedItem?.label.slice(0, 10) + '...'
                                : selectedItem?.label
                              : ''}
                          </span>
                        </Tooltip>
                      );
                    }}
                  >
                    {languageItems.map((item, index) => (
                      <MenuItem key={index} value={item?.label}>
                        {item?.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FieldContainer>

              <FieldContainer width={'100%'} className="allCreateMyLession allCreateMyLessionStory">
                <VocContainer>
                  <label style={{ whiteSpace: 'nowrap' }}>Add Your Vocab</label>
                  <StyledAutocomplete
                    multiple
                    freeSolo
                    id="tags-outlined"
                    options={[]}
                    value={currentTokens}
                    inputValue={vocabInput}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === 'input') {
                        if (newInputValue.includes(',')) {
                          commitTokens(newInputValue.split(','));
                          setVocabInput('');
                        } else {
                          setVocabInput(newInputValue);
                        }
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === ',') {
                        event.preventDefault();
                        if (vocabInput.trim()) {
                          commitTokens([vocabInput]);
                          setVocabInput('');
                        }
                      }
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        if (vocabInput.trim()) {
                          commitTokens([vocabInput]);
                          setVocabInput('');
                        }
                      }
                    }}
                    onChange={(event, value) => {
                      if (value.length < currentTokens.length) {
                        setOwnVocab(value.join(','));
                      } else {
                        const newWords = value.filter((word) => !currentTokens.includes(word));
                        if (newWords.length > 0) {
                          commitTokens(newWords);
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                        }}
                        InputLabelProps={{ shrink: true }}
                        type="text"
                        placeholder="Optional."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          key={index}
                          sx={{
                            marginRight: '5px',
                            marginBottom: '0',
                          }}
                        />
                      ))
                    }
                    PopperComponent={() => null}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: 0,
                        border: 'none',
                        '& fieldset': {
                          display: 'none',
                        },
                      },
                      '& .MuiInputBase-root::before, & .MuiInputBase-root::after': {
                        display: 'none !important',
                      },
                      '& .MuiAutocomplete-listbox': {
                        display: 'flex',
                        flexDirection: 'row',
                        overflowX: 'auto',
                        maxWidth: '100%',
                        whiteSpace: 'nowrap',
                      },
                      '& .MuiAutocomplete-tag': {
                        marginRight: '5px',
                        marginBottom: '0',
                      },
                    }}
                  />
                </VocContainer>
              </FieldContainer>

              <LessonContainer>
                <Label>Story Plot</Label>
                <StyledStoryTextField
                  minRows={3}
                  value={storyPlot}
                  onChange={(event) => setStoryPlot(event.target.value)}
                  multiline
                  fullWidth
                  variant="outlined"
                  placeholder="You can leave this blank if you would like forus to create a plot for you."
                />
              </LessonContainer>

              <div className="w-full flex items-end justify-end mt-10px">
                <BadgeButton
                  disabled={!(gradeLevel && genres && wordCount && proficiencyLevel)}
                  onClick={GenerateStory}
                >
                  Generate Story
                </BadgeButton>
              </div>
            </>
        </ModalContentWrapper>
      </Modal>

      <DeleteModal
        deleteToggle={deleteModal}
        setDeleteToggle={setDeleteModal}
        message="Are you sure you want to delete this story? This action cannot be undone."
        onDelete={onDeleteStory}
        loading={deleteLoader}
      />
    </SentryWrap>
  );
};
export default SoyHistoriasAdmin;
